import React, { useMemo } from "react";

import { Constants, useMeeting, useParticipant } from "../../config/sdkConfig";

import { MemoizedParticipantGrid } from "../../components/ParticipantGrid";

function WebinarParticipantsViewer({ isPresenting, layout, isFlexCol }) {
    const {
        participants,
        pinnedParticipants,
        activeSpeakerId,
        localParticipant,
        localScreenShareOn,
        presenterId,
    } = useMeeting();

    // Only show participants confirmed as stage roles in webinar grid.
    // Remote participants default to SEND_AND_RECV until metadata arrives,
    // so we must check the role metadata as the source of truth.
    const STAGE_ROLES = ['moderator', 'co-host', 'host', 'presenter', 'speaker'];
    const isOnStage = (id) => {
        const p = participants.get(id);
        if (!p) return false;
        // Local participant: trust the mode directly (set from SiteContext)
        if (p.isLocal) {
            return p.mode === Constants.modes.SEND_AND_RECV;
        }
        // Remote participants: require confirmed stage role in metadata.
        // If metadata hasn't arrived yet (meetingRole undefined), check if they
        // have published tracks — only publishers can be on stage.
        const role = p.metaData?.meetingRole;
        if (role) {
            return STAGE_ROLES.includes(role);
        }
        // No role metadata yet — show only if they have tracks (likely a host/presenter)
        return p.webcamOn || p.micOn || p.screenShareOn;
    };

    const participantIds = useMemo(() => {
        // Guard: if localParticipant is not yet available (e.g., during disconnect), return empty
        if (!localParticipant) return [];

        // Filter participants to only include those on stage (SEND_AND_RECV mode or stage role)
        const allParticipantIds = [localParticipant.id, ...participants.keys()];
        const presenterIds = allParticipantIds.filter((participantId) => {
            return (
                participantId === localParticipant.id ||
                isOnStage(participantId)
            );
        });

        const pinnedParticipantId = [...pinnedParticipants.keys()].filter(
            (participantId) => {
                return (
                    participantId !== localParticipant.id &&
                    isOnStage(participantId)
                );
            }
        );

        const regularParticipantIds = presenterIds.filter(
            (participantId) => {
                return (
                    ![...pinnedParticipants.keys()].includes(participantId) &&
                    localParticipant.id !== participantId
                );
            }
        );

        // Only show stage participants (SEND_AND_RECV mode or stage role)
        const ids = [
            ...(localParticipant.mode === Constants.modes.SEND_AND_RECV ||
                STAGE_ROLES.includes(localParticipant?.metaData?.meetingRole)
                ? [localParticipant.id]
                : []),
            ...pinnedParticipantId,
            ...regularParticipantIds,
        ];

        if (activeSpeakerId) {
            if (
                !ids.includes(activeSpeakerId) &&
                isOnStage(activeSpeakerId)
            ) {
                ids[ids.length - 1] = activeSpeakerId;
            }
        }

        return ids;
    }, [
        participants,
        activeSpeakerId,
        pinnedParticipants,
        presenterId,
        localScreenShareOn,
        localParticipant,
    ]);

    return (
        <MemoizedParticipantGrid
            participantIds={participantIds}
            isPresenting={isPresenting}
            layout={layout}
            isFlexCol={isFlexCol}
        />
    );
}

const MemorizedWebinarParticipantView = React.memo(
    WebinarParticipantsViewer,
    (prevProps, nextProps) => {
        return prevProps.isPresenting === nextProps.isPresenting &&
            prevProps.layout === nextProps.layout &&
            prevProps.isFlexCol === nextProps.isFlexCol;
    }
);

export default MemorizedWebinarParticipantView;

